/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import * as consts from '../../const';
import { generateIteratorKey as keygen } from '../../utils/helpers';
import {
  ACTIVITY_TYPE_LABELS as activityTypeLabels,
  ACTIVITY_TYPE_ICONS as activityTypeIcons,
  JOURNAL_EVENT_TYPE_ICONS as journalEventTypeIcons,
  ICONS_COLORS as iconsColors
} from './consenting.journal.activity.types';
import DocumentsService from '../documents/documents.service';
import BiosampleAddIcon from '../../assets/images/icons/biosample-add.svg';
import BiosampleUpdateIcon from '../../assets/images/icons/biosample-update.svg';
import BiosampleDeleteIcon from '../../assets/images/icons/biosample-delete.svg';
import { MANUAL_MESSAGE, MANUAL_VERSION_OPERATION, JOURNAL_REASON, DOCUMENT_REMOVED_MESSAGE } from '../../const';
import { Time } from 'tc-client-common';
import moment from 'moment-timezone/builds/moment-timezone-with-data';

class ConsentingJournalService {
  static MAX_LENGTH = 70;

  static createDetailsGrid(props) {
    const { response, openFlagModal, timezone } = props;
    let header = (
      <Row className="consenting-journal-header">
        <Col xs={2}>{consts.TIME}</Col>
        <Col xs={3}>{consts.EVENT}</Col>
        <Col xs={7}>{consts.DETAILS}</Col>
      </Row>
    );
    let rows = [],
      date = '';
    if (response && response.data) {
      const responseData = response.data.journal || response.data;
      for (let v of responseData.data) {
        const formatString = 'DD MMM YYYY';
        const dayOfEvent = moment(v.timestamp)
          .tz(timezone || 'UTC')
          .format(formatString);
        if (date !== dayOfEvent) {
          date = dayOfEvent;
          rows.push(
            <Row {...keygen()} className="consenting-journal-row journal-date-row">
              <Col xs={10}>
                {consts.TIMESTAMP}: <Time timezone={timezone} timestamp={v.timestamp} format={formatString} offset />
              </Col>
            </Row>
          );
        }

        let typeColumn = this.getDetailsColumn(v);
        const flaggedForm = (v.type === 'FLAG' || v.action === 'FA') && v.fragment && v.fragment.type === 'form';
        rows.push(
          !flaggedForm ? (
            <Row {...keygen()} className="consenting-journal-row">
              <Col xs={2}>
                <Time timezone={timezone} timestamp={v.timestamp} format={'hh:mm A'} />
              </Col>
              {typeColumn}
              <Col xs={7}>{this.formatDetails(v, openFlagModal)}</Col>
            </Row>
          ) : null
        );
      }
    }

    return (
      <Grid id="consenting-journal-grid" fluid className="tc-grid">
        {header}
        <div id="journal-wrapper">{rows}</div>
      </Grid>
    );
  }

  static strip(html) {
    let tmp = document.createElement('DIV');
    let reg = new RegExp('</p><p>', 'g');
    html = html.replace(reg, '\n');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  static breakLine(text) {
    let br = React.createElement('br');
    let regex = /(\r\n|\r|\n)/g;
    return text.split(regex).map(function (line, index) {
      return line.match(regex) ? <br key={'key_' + index} /> : line;
    });
  }

  static construct(fragment, openModal) {
    let short, full;
    switch (fragment.type) {
      case 'html':
        let content = this.strip(fragment.content);
        if (content.length > this.MAX_LENGTH) {
          full = <div dangerouslySetInnerHTML={{ __html: fragment.content }} />; //since the content of the fragment comes directly from the consent, the risk is minimal and we decided to allow dangerouslySetInnerHTML
          short = this.breakLine(content.substring(0, this.MAX_LENGTH) + '...');
          short.push(
            <Button bsStyle="link" onClick={() => openModal(full, consts.FLAGGED_PARAGRAPH)}>
              {consts.VIEW_PARAGRAPH}
            </Button>
          );
        } else {
          short = this.breakLine(content);
        }
        break;
      case 'image':
        full = (
          <div>
            <img src={fragment.images[0]} />
            <p>{this.breakLine(this.strip(fragment.caption))}</p>
          </div>
        );
        short = (
          <div>
            {fragment.caption}
            <br />
            {fragment.images[0]}
            <Button bsStyle="link" onClick={() => openModal(full, consts.FLAGGED_IMAGE)}>
              {consts.VIEW_IMAGE}
            </Button>
          </div>
        );
        break;
      case 'video':
        full = (
          <div>
            <video width="517" height="291" controls>
              <source src={fragment.videoUrls[0]} type="video/mp4" />
              <source src={fragment.videoUrls[0]} type="video/webm" />
              <source src={fragment.videoUrls[0]} type="video/ogg" />
            </video>
            <p>{this.breakLine(this.strip(fragment.caption))}</p>
          </div>
        );
        short = (
          <div>
            {fragment.caption}
            <br />
            {fragment.videoUrls[0]}
            <Button bsStyle="link" onClick={() => openModal(full, consts.FLAGGED_VIDEO)}>
              {consts.VIEW_VIDEO}
            </Button>
          </div>
        );
        break;
      default:
        (short = ''), (full = '');
    }
    return short;
  }

  static buildVersionMessage(entry) {
    return (
      MANUAL_MESSAGE(
        DocumentsService.buildDocumentFullName(entry.documentName, entry.documentVersion),
        MANUAL_VERSION_OPERATION[entry.type],
        entry.author
      ) + JOURNAL_REASON(entry.reason)
    );
  }
  static buildDocumentRemovedMessage(entry) {
    const removeMessage = DOCUMENT_REMOVED_MESSAGE(entry.documentName, entry.author);
    const reasonMessage = JOURNAL_REASON(entry.reason);
    return [removeMessage, reasonMessage].join(' ');
  }

  static formatDetails(entry, openFlagModal) {
    switch (entry.type) {
      case 'VERSION_ADDED':
      case 'VERSION_REMOVED':
        return <div>{this.buildVersionMessage(entry)}</div>;
      case 'DOCUMENT_REMOVED':
        return <div>{this.buildDocumentRemovedMessage(entry)}</div>;
      case 'FLAG':
        if (entry.fragment === null || entry.fragment === '' || entry.fragment === 'null') return '';
        const fragment = entry.fragment;
        const content = this.construct(fragment, openFlagModal);
        let owner = consts.USER + ' ' + entry.owner + ' ';
        return (
          <div>
            {entry.flagged
              ? owner + consts.PARTICIPANT_FLAGGED[fragment.type]
              : owner + consts.PARTICIPANT_UNFLAGGED[fragment.type]}
            <br />
            {content}
          </div>
        );
      case 'ACTIVITY':
        if (entry.action === 'FA') {
          if (entry.fragment === null || entry.fragment === '' || entry.fragment === 'null') return '';
          const fragment = entry.fragment;
          const content = this.construct(fragment, openFlagModal);
          let owner = consts.USER + ' ' + entry.owner + ' ';
          return (
            <div>
              {owner + consts.FLAG_ANSWERED[fragment.type]}
              <br />
              {content}
            </div>
          );
        }
      default:
        return this.breakLine(entry.details);
    }
  }

  static getDetailsColumn(entry) {
    let iconCssClass = '';
    let iconColor = '';
    let iconElement = null;
    let typeColumn = null;
    if (entry.type === 'ACTIVITY') {
      iconCssClass = activityTypeIcons[entry.action];
      iconColor = iconsColors[entry.action];
      iconElement = (
        <i className={'material-icons padding-right-15 ' + iconCssClass + ' ' + iconColor}>{iconCssClass}</i>
      );
      typeColumn = (
        <Col className="padding-right-15" xs={3}>
          {iconElement}
          {activityTypeLabels[entry.action]}
        </Col>
      );
    } else {
      console.log(entry);
      if (entry.type === 'FLAG' && entry.flagged === false) {
        iconCssClass = journalEventTypeIcons['UNFLAG'];
        iconColor = iconsColors['UNFLAG'];
      } else {
        iconCssClass = journalEventTypeIcons[entry.type];
        iconColor = iconsColors[entry.type];
      }
      if (entry.type === 'BIOSAMPLE_ADDED') {
        iconElement = (
          <i className="material-icons padding-right-15">
            <img src={BiosampleAddIcon} />
          </i>
        );
      } else if (entry.type === 'BIOSAMPLE_UPDATED') {
        iconElement = (
          <i className="material-icons padding-right-15">
            <img src={BiosampleUpdateIcon} />
          </i>
        );
      } else if (entry.type === 'BIOSAMPLE_REMOVED') {
        iconElement = (
          <i className="material-icons padding-right-15">
            <img src={BiosampleDeleteIcon} />
          </i>
        );
      } else {
        iconElement = (
          <i className={'material-icons padding-right-15 ' + iconCssClass + ' ' + iconColor}>{iconCssClass}</i>
        );
      }
      typeColumn = (
        <Col className="padding-right-15" xs={3}>
          {iconElement}
          {consts.JOURNAL_CONSTANTS[entry.type] || consts[entry.type]}
        </Col>
      );
    }
    return typeColumn;
  }
}

export default ConsentingJournalService;
